import React from 'react';
import {ReactWizard, WizardPhase} from "../../../components/ReactWizard/ReactWizard";
import Utils from "../../../common/Utils";
import {BusyContent, HeadView, LoadingBars, RegisterComponent} from "../RegisterBase";
import {Alert, Button, NavLink} from "reactstrap";
import FormInput from "../../../components/FormInput/FormInput";
import {UsersEndpoints} from "../../../common/ProxyEndpoints";
import {Form, Formik} from "formik";
import * as Yup from 'yup';
import DocumentTitle from 'react-document-title';
import AppVariables from '../../../common/AppVariables.js';
import {ActivationFormSchema} from "../../ActivateAccount";
import PlatformRender from "../../../components/PlatformRender/PlatformRender";
import {DevicePlatforms} from "../../../common/Constants";
import CodeInputView from "../../../components/CodeInputView/CodeInputView";

const RegisterHead = (props) => {
    return <HeadView title="Register" message="FBO (Farmer Based Organization)" info={props.phaseInfo}/>;
};

const AccountInfoSchema = Yup.object().shape({
    username: Yup.string()
        .required('Please enter a valid username')
        .min(AppVariables.MinUsernameLength)
        .matches(/[a-z]+[a-z0-9@_.\-]+$/,"Username cannot be numeric or contain special characters and whitespaces except \"@\", \".\", \"_\"")
        .matches(/[a-z0-9]$/,"Username can only end with a letter or number")
        .label("Username"),
    password: Yup.string().required('Please enter your password').min(AppVariables.MinPasswordLength).label("Password"),
    password_confirm: Yup.string().required('Confirm password is required').oneOf([Yup.ref('password'),null],"Passwords do not match").label("Confirm Password")
});

const CompanyInfoSchema = Yup.object().shape({
    name: Yup.string()
        .trim()
        .required('Please enter a valid name')
        .matches(/[a-zA-Z]{1,}/,"Invalid name. Cannot begin with a number or special characters")
        .matches(/[a-zA-Z0-9]$/,"Name cannot end with symbols or any special character")
        .label('Name'),
    email: Yup.string().email().trim().label('Email'),
    phone: Yup.string()
        .required('Enter a valid phone number')
        .test('phone','Must be a valid phone number', val => Utils.validatePhoneNumber(val))
        .label('Phone'),
    location: Yup.object().nullable().required('Please enter your location').label('Location')
});


class RegisterFBO extends RegisterComponent{

    constructor(props){
        super(props, 'register_fbo');

        this.setPhaseInfo({

            account_info: {
                index: 0,
                getForm: () => this._accountInfoForm,
                validate: this.onVerifyAccountInfo.bind(this)
            },

            company_info: {
                index: 1,
                getForm: () => this._companyInfoForm,
                validate: this.onVerifyCompanyInfo.bind(this)
            },

            create_account: {
                index: 2
            },

            activate_account: {
                index: 3,
                getForm: () => this._activateForm,
                validate: this.onActivateAccount.bind(this)
            }

        });

    }

    onVerifyAccountInfo(){

        const  { password_confirm, ...accountInfo } = this._accountInfoForm.getFormikContext().values;
        this.persistState('accountInfo',accountInfo);
        return this.processValidation( UsersEndpoints.registerFBOAccount(true,false,accountInfo) );
    }

    onVerifyCompanyInfo(){

        const { name , email , phone , location } = this._companyInfoForm.getFormikContext().values;

        const companyInfo = {
            phone: Utils.fixPhone(phone),
            email,
            fbo: {
                name,
                location: location.text,
                location_lat: location.lat,
                location_long: location.lng,
            }
        };

        this.persistState('companyInfo',companyInfo);
        return this.processValidation(UsersEndpoints.registerFBOAccount(true,false,companyInfo));
    }

    onCreateAccount(){

        const payload = {
            ...this.getStateValue('accountInfo'),
            ...this.getStateValue('companyInfo')
        };

        return this.processRegister(UsersEndpoints.registerFBOAccount(false,true,payload));
    }

    onActivateAccount(){
        const { activation_code } = this._activateForm.getFormikContext().values;
        const { username , password } = this.getStateValue('accountInfo');
        return this.processAccountActivation(activation_code,username,password);
    }

    wizardRender(){

        return (<DocumentTitle title={"Register - FBO"}>
                <ReactWizard ref={(wizard) => this._wizard = wizard} sessionKey="register_fbo_wz_session">

                    {/*Account info phase*/}
                    <WizardPhase name="Account Info" >

                        <Formik validationSchema={AccountInfoSchema}
                                initialValues={{ username: '', password: '', password_confirm: ''  }}
                                onSubmit={async (values, { setSubmitting }) => {
                                    await this.moveNextPhase();
                                    setSubmitting(false);
                                }}
                                ref={r => this._accountInfoForm = r}>

                            {({ isValid }) => (<Form className="card-block p-4 animated fadeIn">

                                <RegisterHead phaseInfo="ACCOUNT INFO"/>

                                <div>

                                    {/*Progress indicator here*/}
                                    <FormInput  name="username"
                                                type="text"
                                                title="Username"
                                                disabled={this.state.isBusy}
                                                required={true}
                                                icon="fa fa-user"
                                                placeholder="Enter your account username here..."
                                                showPrompt={true}/>

                                    <FormInput  name="password"
                                                type="password"
                                                required={true}
                                                title="Password"
                                                disabled={this.state.isBusy}
                                                icon="fa fa-lock"
                                                placeholder="Enter your password here..."
                                                showPrompt={true}/>

                                    <FormInput  name="password_confirm"
                                                type="password"
                                                required={true}
                                                disabled={this.state.isBusy}
                                                title="Confirm password"
                                                icon="fa fa-lock"
                                                placeholder="Confirm your password..."
                                                showPrompt/>

                                </div>

                                {this.renderCurrentError()}

                                {this.state.isBusy && <LoadingBars/>}

                                <Button type="submit" color="green" disabled={!isValid || this.state.isBusy} block>Continue <i className="icon-arrow-right"></i></Button>

                                <div className="text-center" style={{'marginTop': '10px'}}>
                                    <NavLink href="#" onClick={Utils.navigateLink.bind(this,'/login')}> <i className="icon-arrow-left"></i> <b>Already have an account</b></NavLink>
                                </div>

                            </Form>)}
                        </Formik>

                    </WizardPhase>


                    {/*Company info phase*/}
                    <WizardPhase name="Company Info">

                        <Formik initialValues={{ name: '' , phone: '' , location: null, email: '' }}
                                validationSchema={CompanyInfoSchema}
                                onSubmit={async (values ,{ setSubmitting }) => {
                                    await this.moveNextPhase();
                                    setSubmitting(true);
                                }}
                                ref={r => this._companyInfoForm = r}>
                            {({ isValid }) => (<Form className="card-block p-4 animated fadeIn">

                                <RegisterHead phaseInfo="ORGANIZATION INFO"/>

                                <div>

                                    {/*Progress indicator here*/}
                                    <FormInput  name="name"
                                                type="text"
                                                title="Organization Name"
                                                required={true}
                                                icon="fa fa-user"
                                                disabled={this.state.isBusy}
                                                showPrompt={true}
                                                placeholder="Enter organization name here..."/>

                                    <FormInput  name="phone"
                                                type="phone"
                                                title="Phone"
                                                required={true}
                                                disabled={this.state.isBusy}
                                                icon="fa fa-phone"
                                                showPrompt={true}
                                                placeholder="Enter your phone number here..."/>

                                    <FormInput  name="location"
                                                type="location"
                                                title="Location"
                                                disabled={this.state.isBusy}
                                                required={true}
                                                showPrompt={true}
                                                icon="fa fa-map-marker"
                                                placeholder="Enter your organization's location..."/>

                                    <hr/>

                                    <FormInput  name="email"
                                                type="email"
                                                title="Email"
                                                icon="fa fa-envelope"
                                                disabled={this.state.isBusy}
                                                required={false}
                                                showPrompt={true}
                                                placeholder="Enter your email here..."/>


                                </div>

                                {this.renderCurrentError()}

                                {this.state.isBusy && <LoadingBars/>}

                                <Button type="submit" color="green" disabled={!isValid || this.state.isBusy} block><i className="fa fa-user-plus"></i> Register</Button>

                                <div className="text-center" style={{'marginTop': '10px'}}>
                                    <NavLink href="#" onClick={this.movePreviousPhase}><i className="icon-arrow-left"></i> <b>Back to account info</b></NavLink>
                                </div>

                            </Form>)}
                        </Formik>
                    </WizardPhase>

                    {/* Create account phase */}
                    <WizardPhase name="RegisterFBO" onActivate={() => this.onCreateAccount() }>

                        <div className="card-block p-4 animated fadeIn" >
                            <BusyContent title="Processing account" message="Please hold on while we prepare your account..." />
                        </div>

                    </WizardPhase>

                    {/*Activate user account*/}
                    <WizardPhase name="Activate Account">

                        <div className="card-block p-4 animated fadeIn">

                            <Formik initialValues={{ activation_code: '' }}
                                    validationSchema={ActivationFormSchema}
                                    onSubmit={async (values ,{ setSubmitting }) => {
                                        await this.moveNextPhase();
                                        setSubmitting(true);
                                    }}
                                    ref={r => this._activateForm = r}>

                                {({ isValid, isSubmitting }) => (<Form>

                                    <div className="text-center">
                                        <PlatformRender platform={DevicePlatforms.Desktop}>
                                            <h3>Activate Account</h3>
                                        </PlatformRender>

                                        <PlatformRender platform={DevicePlatforms.Mobile}>
                                            <h4>Activate Account</h4>
                                        </PlatformRender>

                                        <p>Just one more step to go...</p>

                                        <hr/>
                                        <Alert color="info"><b>Hint:</b> You've been sent <b>activation code</b>. Enter the code below to proceed.</Alert>

                                    </div>

                                    <CodeInputView name={"activation_code"} size={0.8} className="mb-2"/>

                                    <div>
                                        {this.state.isBusy && <LoadingBars/>}
                                    </div>

                                    {this.renderCurrentError()}

                                    <Button type="submit" color="green" disabled={!isValid || this.state.isBusy} block>
                                        Activate Account <i className="fa fa-check"></i>
                                    </Button>

                                    <div className="text-center" style={{'marginTop': '10px'}}>
                                        <NavLink href="#" onClick={Utils.navigateLink.bind(this,'/login')}> <i className="icon-arrow-left"></i> <b>Back to Login</b></NavLink>
                                    </div>

                                </Form>)}
                            </Formik>


                        </div>

                    </WizardPhase>

                </ReactWizard>
            </DocumentTitle>)

    };

}

export default  RegisterFBO;


// WEBPACK FOOTER //
// ./src/views/Register/FBO/RegisterFBO.js